import 'angular';
import 'angular-route';
import 'angular-touch';
import 'angular-animate';
import 'angular-ui-bootstrap';
import 'angularjs-toaster';
import 'angular-sanitize';
import { homeRoutes } from '@app/routes';
import LoggerService from '@app/service/logger.service';

import initializeGlobalErrorHandler from '@app/service/globalErrorHandler.ts';
import initializeRouteLogging from '@app/service/routeLogging.ts';
import initializeHttpErrorInterceptor from '@app/interceptor/http/error/httpInterceptor.ts';
import { POPOVER_TEMPLATE_URL } from '@app/components/core/popover/popover';
import popoverHtml from '@app/components/core/popover/popover.html';

var routes = {
  ...homeRoutes,
  ADDON: {
    path: '/addon',
    template: async () => getTemplate(import('html/addon/addon.html')),
    controller: 'AddOnCtrl',
    resolve: {
      loadController: () => import('chunk/controller/AddOnCtrl.js')
    },
    controllerAs: 'addon',
    gotoName: 'goToAddOn',
    addOnRequired: true
  },
  ADDON_DETAIL: {
    path: '/addon-detail/:skuCode',
    template: async () => getTemplate(import('html/addon/customize-addon-detail.html')),
    controller: 'AddOnDetailCtrl',
    resolve: {
      loadController: () => import('chunk/controller/AddOnDetailCtrl.js')
    },
    controllerAs: 'addOnDetail',
    addOnRequired: true,
    gotoName: 'goToAddOnDetail',
    gotoFunc: function (RouterService) {
      return function (skuCode) {
        RouterService.goTo(routes.ADDON_DETAIL.path.replace(':skuCode', skuCode))();
      };
    }
  },
  ADDON_DETAIL_EDIT: {
    path: '/addon-detail/edit/:editIndex',
    template: async () => getTemplate(import('html/addon/customize-addon-detail.html')),
    controller: 'AddOnDetailCtrl',
    resolve: {
      loadController: () => import('chunk/controller/AddOnDetailCtrl.js')
    },
    controllerAs: 'addOnDetail',
    addOnRequired: true,
    gotoName: 'goToEditAddOnDetail',
    gotoFunc: function (RouterService) {
      return function (editIndex) {
        RouterService.goTo(routes.ADDON_DETAIL_EDIT.path.replace(':editIndex', editIndex))();
      };
    }
  },
  BILLING_INFO: {
    path: '/billing-info',
    template: async () => getTemplate(import('html/checkout/billing-info.html')),
    controller: 'BillingInfoCtrl',
    resolve: {
      loadController: () => import('chunk/controller/BillingInfoCtrl.js')
    },
    controllerAs: 'billingInfo',
    gotoName: 'goToBillingInfo',
    orderFlow: false
  },
  BILLING_INFO_WITH_MESSAGE: {
    path: '/billing-info/:message',
    template: async () => getTemplate(import('html/checkout/billing-info.html')),
    controller: 'BillingInfoCtrl',
    resolve: {
      loadController: () => import('chunk/controller/BillingInfoCtrl.js')
    },
    controllerAs: 'billingInfo',
    gotoName: 'goToBillingInfoWithMessage',
    orderFlow: false,
    gotoFunc: function (RouterService) {
      return function (message) {
        RouterService.goTo(routes.BILLING_INFO_WITH_MESSAGE.path.replace(':message', message))();
      };
    }
  },
  BUNDLE_DETAIL: {
    path: '/bundle-detail/:skuCode',
    template: async () => getTemplate(import('html/customize/detail.html')),
    controller: 'BundleDetailCtrl',
    resolve: {
      loadController: () => import('chunk/controller/BundleDetailCtrl.js')
    },
    controllerAs: 'customize',
    bundleRequired: true,
    gotoName: 'goToBundleDetail',
    gotoFunc: function (RouterService) {
      return function (skuCode) {
        RouterService.goTo(routes.BUNDLE_DETAIL.path.replace(':skuCode', skuCode))();
      };
    }
  },
  BUNDLE_DETAIL_EDIT: {
    path: '/bundle-detail/edit/:editIndex',
    template: async () => getTemplate(import('html/customize/detail.html')),
    controller: 'BundleDetailCtrl',
    resolve: {
      loadController: () => import('chunk/controller/BundleDetailCtrl.js')
    },
    controllerAs: 'customize',
    bundleRequired: true,
    gotoName: 'goToEditBundleDetail',
    gotoFunc: function (RouterService) {
      return function (editIndex) {
        RouterService.goTo(routes.BUNDLE_DETAIL_EDIT.path.replace(':editIndex', editIndex))();
      };
    }
  },
  CHANGE_PASSWORD: {
    path: '/change-password',
    template: async () => getTemplate(import('html/account/changePassword.html')),
    controller: 'ChangePasswordCtrl',
    resolve: {
      loadController: () => import('chunk/controller/ChangePasswordCtrl.js')
    },
    controllerAs: 'changePassword',
    jobRequired: false,
    orderRequired: false,
    orderFlow: false,
    elevationRequired: true, // check elevation before init as it is not handled on SFLY side
    gotoName: 'goToChangePassword'
  },
  CREATE_ACCOUNT: {
    path: '/create-account',
    template: async () => getTemplate(import('html/account/create-account.html')),
    controller: 'CreateAccountCtrl',
    controllerAs: 'createAccountCtrl',
    jobRequired: false,
    orderRequired: false,
    orderFlow: false,
    gotoName: 'goToCreateAccount'
  },
  CYOP: {
    path: '/cyop',
    template: async () => getTemplate(import('html/cyop/cyop.html')),
    controller: 'CYOPCtrl',
    resolve: {
      loadController: () => import('chunk/controller/CYOPCtrl.js')
    },
    controllerAs: 'cyop',
    cyopRequired: true,
    gotoName: 'goToCYOP'
  },
  CYOP_EDIT: {
    path: '/cyop/edit/:editIndex',
    template: async () => getTemplate(import('html/cyop/cyop.html')),
    controller: 'CYOPCtrl',
    resolve: {
      loadController: () => import('chunk/controller/CYOPCtrl.js')
    },
    controllerAs: 'cyop',
    cyopRequired: true,
    gotoName: 'goToEditCYOP',
    gotoFunc: function (RouterService) {
      return function (editIndex) {
        RouterService.goTo(routes.CYOP_EDIT.path.replace(':editIndex', editIndex))();
      };
    }
  },
  MY_ACCOUNT: {
    path: '/my-account/',
    template: `<lt-my-account></lt-my-account>`,
    resolve: {
      loadComponent: () => import('app/modules/my-account/my-account.component.ts')
    },
    jobRequired: false,
    loginRequired: true,
    elevationRequired: true,
    orderRequired: false,
    orderFlow: false,
    gotoName: 'goToMyAccount'
  },
  DIGITAL_DOWNLOAD: {
    path: '/downloads',
    template: async () => getTemplate(import('html/digital-download/didDownload.html')),
    controller: 'DIDDownloadCtrl',
    resolve: {
      loadController: () => import('chunk/controller/DIDDownloadCtrl.js')
    },
    controllerAs: 'didDownload',
    jobRequired: false,
    orderRequired: false,
    orderFlow: false,
    gotoName: 'goToDIDDownload'
  },
  DID_ERROR: {
    path: '/didError/:identifier',
    template: async () => getTemplate(import('html/digital-download/downloadError.html')),
    controller: 'DIDErrorCtrl',
    resolve: {
      loadController: () => import('chunk/controller/DIDErrorCtrl.js')
    },
    controllerAs: 'downloadError',
    jobRequired: false,
    orderRequired: false,
    orderFlow: false,
    gotoName: 'goToDIDError',
    gotoFunc: function (RouterService) {
      return function (identifier) {
        RouterService.goTo(routes.DID_ERROR.path.replace(':identifier', identifier))();
      };
    }
  },
  DIGITAL_DOWNLOAD_CLAIM: {
    path: '/downloads/:identifier',
    template: async () => getTemplate(import('html/digital-download/digitalDownload.html')),
    controller: 'DIDTermsCtrl',
    resolve: {
      loadController: () => import('chunk/controller/DIDTermsCtrl.js')
    },
    controllerAs: 'digitalDownload',
    jobRequired: false,
    orderRequired: false,
    orderFlow: false,
    gotoName: 'goToDIDTerms',
    gotoFunc: function (RouterService) {
      return function (identifier) {
        RouterService.goTo(routes.DIGITAL_DOWNLOAD_CLAIM.path.replace(':identifier', identifier))();
      };
    }
  },
  SAFETY_DOWNLOAD: {
    path: '/safety-download',
    template: async () => getTemplate(import('html/digital-download/dsskDownload.html')),
    controller: 'DSSKDownloadCtrl',
    resolve: {
      loadController: () => import('chunk/controller/DSSKDownloadCtrl.js')
    },
    controllerAs: 'dsskDownload',
    jobRequired: false,
    orderRequired: false,
    orderFlow: false,
    gotoName: 'goToDSSKDownload'
  },
  DSSK_ERROR: {
    path: '/dsskError/:identifier',
    template: async () => getTemplate(import('html/digital-download/downloadError.html')),
    controller: 'DSSKErrorCtrl',
    resolve: {
      loadController: () => import('chunk/controller/DSSKErrorCtrl.js')
    },
    controllerAs: 'downloadError',
    jobRequired: false,
    orderRequired: false,
    orderFlow: false,
    gotoName: 'goToDSSKError',
    gotoFunc: function (RouterService) {
      return function (identifier) {
        RouterService.goTo(routes.DSSK_ERROR.path.replace(':identifier', identifier))();
      };
    }
  },
  FIND_SCHOOL: {
    path: '/school-finder',
    template: async () => getTemplate(import('html/claim/find-school.html')),
    controller: 'FindSchoolCtrl',
    resolve: {
      loadController: () => import('chunk/controller/FindSchoolCtrl.js')
    },
    controllerAs: 'findSchool',
    jobRequired: false,
    orderRequired: false,
    orderFlow: false,
    elevationRequired: true,
    gotoName: 'goToFindSchool'
  },
  FOUND_MATCH: {
    path: '/we-found-a-match',
    template: async () => getTemplate(import('html/claim/found-match.html')),
    controller: 'FoundMatchCtrl',
    resolve: {
      loadController: () => import('chunk/controller/FoundMatchCtrl.js')
    },
    controllerAs: 'foundMatch',
    jobRequired: false,
    orderRequired: false,
    orderFlow: false,
    elevationRequired: false,
    gotoName: 'goToFoundMatch',
    gotoFunc: function (RouterService) {
      return function (studentId) {
        RouterService.goTo(routes.FOUND_MATCH.path + (studentId ? '?studentIdPath=' + studentId : ''))();
      };
    }
  },
  LINK_SENT: {
    path: '/link-sent',
    template: async () => getTemplate(import('html/login/linkSent.html')),
    controller: 'LinkSentCtrl',
    resolve: {
      loadController: () => import('chunk/controller/LinkSentCtrl.js')
    },
    controllerAs: 'linkSentCtrl',
    jobRequired: false,
    orderRequired: false,
    orderFlow: false,
    gotoName: 'goToLinkSent',
    gotoFunc: function (RouterService) {
      return function (identifier) {
        RouterService.goTo(routes.LINK_SENT.path.replace(':identifier', identifier))();
      };
    }
  },
  MFR_ENROLLMENT: {
    path: '/loyalty-enrollment',
    template: async () => getTemplate(import('html/account/mfr-enrollment.html')),
    controller: 'MfrEnrollmentCtrl',
    resolve: {
      loadController: () => import('chunk/controller/MfrEnrollmentCtrl.js')
    },
    controllerAs: 'mfrEnrollment',
    jobRequired: false,
    orderRequired: false,
    orderFlow: false,
    gotoName: 'goToMfrEnrollment'
  },
  MFR_EXPLAINER: {
    path: '/explainer',
    template: async () => getTemplate(import('html/mfr/explainer.html')),
    controller: 'MfrExplainerCtrl',
    resolve: {
      loadController: () => import('chunk/controller/MfrExplainerCtrl.js')
    },
    controllerAs: 'mfrExplainer',
    jobRequired: false,
    orderRequired: false,
    orderFlow: false,
    gotoName: 'goToExplainer'
  },
  MY_DASHBOARD: {
    path: '/my-dashboard',
    template: async () => getTemplate(import('html/account/myDashboard.html')),
    controller: 'MyDashboardCtrl',
    resolve: {
      loadController: () => import('chunk/controller/MyDashboardCtrl.js')
    },
    controllerAs: 'myDashboard',
    packageRequired: false,
    jobRequired: false,
    orderRequired: false,
    orderFlow: false,
    elevationRequired: false,
    gotoName: 'goToMyDashboard'
  },
  MY_PROOFS: {
    path: '/my-proofs',
    template: async () => getTemplate(import('html/my-proofs/my-proofs.html')),
    controller: 'MyProofsCtrl',
    resolve: {
      loadController: () => import('chunk/controller/MyProofsCtrl.js')
    },
    controllerAs: 'myProofs',
    packageRequired: false,
    jobRequired: false,
    orderRequired: false,
    orderFlow: false,
    elevationRequired: false,
    gotoName: 'goToMyProofs',
    gotoFunc: function (RouterService) {
      return function (studentId) {
        RouterService.goTo(routes.MY_PROOFS.path + (studentId ? '?studentIdPath=' + studentId : ''))();
      };
    }
  },
  ORDER_DETAIL: {
    path: '/order-detail/:orderId',
    template: async () => getTemplate(import('html/account/orderDetail.html')),
    controller: 'OrderDetailCtrl',
    resolve: {
      loadController: () => import('chunk/controller/OrderDetailCtrl.js')
    },
    controllerAs: 'orderDetail',
    jobRequired: false,
    loginRequired: true,
    elevationRequired: true,
    orderRequired: false,
    orderFlow: false,
    gotoName: 'goToOrderDetail',
    gotoFunc: function (RouterService) {
      return function (orderId) {
        RouterService.goTo(routes.ORDER_DETAIL.path.replace(':orderId', orderId))();
      };
    }
  },
  ORDER_RECEIPT: {
    path: '/order-receipt',
    template: async () => getTemplate(import('html/checkout/order-receipt.html')),
    controller: 'OrderReceiptCtrl',
    resolve: {
      loadController: () => import('chunk/controller/OrderReceiptCtrl.js')
    },
    controllerAs: 'orderReceipt',
    orderRequired: false,
    orderFlow: false,
    gotoName: 'goToOrderReceipt'
  },
  ORDER_RECEIPT_WITH_MESSAGE: {
    path: '/order-receipt/:message',
    template: async () => getTemplate(import('html/checkout/order-receipt.html')),
    controller: 'OrderReceiptCtrl',
    resolve: {
      loadController: () => import('chunk/controller/OrderReceiptCtrl.js')
    },
    controllerAs: 'orderReceipt',
    orderRequired: false,
    orderFlow: false,
    gotoName: 'goToOrderReceiptWithMessage',
    gotoFunc: function (RouterService) {
      return function (message) {
        RouterService.goTo(routes.ORDER_RECEIPT_WITH_MESSAGE.path.replace(':message', message))();
      };
    }
  },
  ORDER_STATUS: {
    path: '/order-status',
    template: async () => getTemplate(import('html/order-status/order-status.html')),
    controller: 'OrderStatusCtrl',
    resolve: {
      loadController: () => import('chunk/controller/OrderStatusCtrl.js')
    },
    controllerAs: 'orderStatus',
    jobRequired: false,
    orderRequired: false,
    orderFlow: false,
    elevationRequired: false,
    gotoName: 'goToOrderStatus',
    gotoFunc: function (RouterService) {
      return function () {
        RouterService.goTo(routes.ORDER_STATUS.path)();
      };
    }
  },
  ORDER_STATUS_LOOKUP: {
    path: '/order-status-lookup',
    template: async () => getTemplate(import('html/order-status/order-status-lookup.html')),
    controller: 'OrderStatusLookupCtrl',
    resolve: {
      loadController: () => import('chunk/controller/OrderStatusLookupCtrl.js')
    },
    controllerAs: 'orderStatusLookup',
    jobRequired: false,
    orderRequired: false,
    orderFlow: false,
    elevationRequired: false,
    gotoName: 'goToOrderStatusLookup'
  },
  ORDER_STATUS_SMART_LINK: {
    path: '/order-status-lookup/:orderNumber/:emailAddress',
    template: async () => getTemplate(import('html/order-status/order-status-lookup.html')),
    controller: 'OrderStatusLookupCtrl',
    resolve: {
      loadController: () => import('chunk/controller/OrderStatusLookupCtrl.js')
    },
    controllerAs: 'orderStatusLookup',
    jobRequired: false,
    orderRequired: false,
    orderFlow: false,
    elevationRequired: false
  },
  PACKAGES: {
    path: '/packages',
    template: async () => getTemplate(import('html/packages/packages.html')),
    controller: 'PackagesCtrl',
    resolve: {
      loadController: () => import('chunk/controller/PackagesCtrl.js')
    },
    controllerAs: 'packages',
    packageRequired: true,
    gotoName: 'goToPackages'
  },
  PACKAGE_DETAIL: {
    path: '/package-detail/:skuCode',
    template: async () => getTemplate(import('html/customize/customize_detail.html')),
    controller: 'PackageDetailCtrl',
    resolve: {
      loadController: () => import('chunk/controller/PackageDetailCtrl.js')
    },
    controllerAs: 'customize',
    packageRequired: true,
    gotoName: 'goToPackageDetail',
    gotoFunc: function (RouterService) {
      return function (skuCode) {
        RouterService.goTo(routes.PACKAGE_DETAIL.path.replace(':skuCode', skuCode))();
      };
    }
  },
  PACKAGE_DETAIL_EDIT: {
    path: '/package-detail/edit/:editIndex',
    template: async () => getTemplate(import('html/customize/customize_detail.html')),
    controller: 'PackageDetailCtrl',
    resolve: {
      loadController: () => import('chunk/controller/PackageDetailCtrl.js')
    },
    controllerAs: 'customize',
    packageRequired: true,
    gotoName: 'goToEditPackageDetail',
    gotoFunc: function (RouterService) {
      return function (editIndex) {
        RouterService.goTo(routes.PACKAGE_DETAIL_EDIT.path.replace(':editIndex', editIndex))();
      };
    }
  },
  PASSWORD_RECOVERY: {
    path: '/password-recovery',
    template: async () => getTemplate(import('html/login/passwordRecovery.html')),
    controller: 'PasswordRecoveryCtrl',
    resolve: {
      loadController: () => import('chunk/controller/PasswordRecoveryCtrl.js')
    },
    controllerAs: 'passwordRecovery',
    jobRequired: false,
    orderRequired: false,
    orderFlow: false,
    gotoName: 'goToPasswordRecovery'
  },
  PASSWORD_RECOVERY_GUEST: {
    path: '/password-recovery/:page',
    template: async () => getTemplate(import('html/login/passwordRecovery.html')),
    controller: 'PasswordRecoveryCtrl',
    resolve: {
      loadController: () => import('chunk/controller/PasswordRecoveryCtrl.js')
    },
    controllerAs: 'passwordRecovery',
    jobRequired: false,
    orderRequired: false,
    orderFlow: false,
    gotoName: 'goToGuestPasswordRecovery',
    gotoFunc: function (RouterService) {
      return function (page) {
        RouterService.goTo(routes.PASSWORD_RECOVERY_GUEST.path.replace(':page', page))();
      };
    }
  },
  PAYMENT_INFO: {
    path: '/payment-info',
    template: async () => getTemplate(import('html/checkout/paymentInfo.html')),
    controller: 'PaymentInfoCtrl',
    resolve: {
      loadController: () => import('chunk/controller/PaymentInfoCtrl.js')
    },
    controllerAs: 'paymentInfo',
    orderFlow: false,
    gotoName: 'goToPaymentInfo'
  },
  PAYMENT_INFO_WITH_MESSAGE: {
    path: '/payment-info/:message',
    template: async () => getTemplate(import('html/checkout/paymentInfo.html')),
    controller: 'PaymentInfoCtrl',
    resolve: {
      loadController: () => import('chunk/controller/PaymentInfoCtrl.js')
    },
    controllerAs: 'paymentInfo',
    orderFlow: false,
    gotoName: 'goToPaymentInfoWithMessage',
    gotoFunc: function (RouterService) {
      return function (message) {
        RouterService.goTo(routes.PAYMENT_INFO_WITH_MESSAGE.path.replace(':message', message))();
      };
    }
  },
  PAY_ONLY: {
    path: '/pay-only',
    template: async () => getTemplate(import('html/pay-only/payOnlyOverview.html')),
    controller: 'PayOnlyCtrl',
    resolve: {
      loadController: () => import('chunk/controller/PayOnlyCtrl.js')
    },
    controllerAs: 'payOnlyOverview',
    jobRequired: false,
    orderRequired: false,
    orderFlow: false,
    gotoName: 'goToPayOnly'
  },
  PICTURE_DAY: {
    path: '/picture-day',
    template: async () => getTemplate(import('html/claim/picture-day.html')),
    controller: 'PictureDayCtrl',
    resolve: {
      loadController: () => import('chunk/controller/PictureDayCtrl.js')
    },
    controllerAs: 'pictureDay',
    jobRequired: false,
    orderRequired: false,
    orderFlow: false,
    elevationRequired: false,
    gotoName: 'goToPictureDay',
    gotoFunc: function (RouterService) {
      return function (from) {
        RouterService.goTo(routes.PICTURE_DAY.path + (from ? '?routeFrom=' + from : ''))();
      };
    }
  },
  PORTRAIT_LOOKS: {
    path: '/portrait-looks/:desc',
    template: async () => getTemplate(import('html/portrait-looks/portrait-looks.html')),
    controller: 'PortraitLooksCtrl',
    resolve: {
      loadController: () => import('chunk/controller/PortraitLooksCtrl.js')
    },
    controllerAs: 'portraitLooks',
    gotoName: 'goToPortraitLooks',
    gotoFunc: function (RouterService) {
      return function (currentItemDesc) {
        RouterService.goTo(routes.PORTRAIT_LOOKS.path.replace(':desc', currentItemDesc))();
      };
    }
  },
  PORTRAIT_LOOKS_FROM_CYOP: {
    path: '/portrait-looks/:desc/:fromPage',
    template: async () => getTemplate(import('html/portrait-looks/portrait-looks.html')),
    controller: 'PortraitLooksCtrl',
    resolve: {
      loadController: () => import('chunk/controller/PortraitLooksCtrl.js')
    },
    controllerAs: 'portraitLooks',
    gotoName: 'goToPortraitLooksFromCYOP',
    gotoFunc: function (RouterService) {
      return function (currentItemDesc, fromPage) {
        RouterService.goTo(
          routes.PORTRAIT_LOOKS_FROM_CYOP.path.replace(':fromPage', fromPage).replace(':desc', currentItemDesc)
        )();
      };
    }
  },
  PREPAY_STUDENT_INFO: {
    path: '/prepay-student-info',
    template: async () => getTemplate(import('html/claim/prepay-student-info.html')),
    controller: 'PrepayStudentInfoCtrl',
    resolve: {
      loadController: () => import('chunk/controller/PrepayStudentInfoCtrl.js')
    },
    controllerAs: 'studentInfo',
    jobRequired: false,
    orderRequired: false,
    orderFlow: false,
    elevationRequired: false,
    gotoName: 'goToPrepayStudentInfo',
    gotoFunc: function (RouterService) {
      return function (routeTo) {
        RouterService.goTo(routes.PREPAY_STUDENT_INFO.path + (routeTo ? '?routeTo=' + routeTo : ''))();
      };
    }
  },
  RESET_PASSWORD: {
    path: '/reset-password',
    template: async () => getTemplate(import('html/login/resetPassword.html')),
    controller: 'ResetPasswordCtrl',
    resolve: {
      loadController: () => import('chunk/controller/ResetPasswordCtrl.js')
    },
    controllerAs: 'resetPassword',
    jobRequired: false,
    orderRequired: false,
    orderFlow: false,
    gotoName: 'goToResetPassword'
  },
  REVIEW_ORDER: {
    path: '/review-order',
    template: async () => getTemplate(import('html/checkout/reviewOrder.html')),
    controller: 'ReviewOrderCtrl',
    resolve: {
      loadController: () => import('chunk/controller/ReviewOrderCtrl.js')
    },
    controllerAs: 'reviewOrder',
    orderFlow: false,
    gotoName: 'goToReviewOrder'
  },
  SHOPPING_CART: {
    path: '/shopping-cart',
    template: async () => getTemplate(import('html/shopping-cart/shopping-cart.html')),
    controller: 'ShoppingCartCtrl',
    resolve: {
      loadController: () => import('chunk/controller/ShoppingCartCtrl.js')
    },
    controllerAs: 'shoppingCart',
    orderFlow: false,
    gotoName: 'goToShoppingCart'
  },
  SMILESAFE_CONFIRMATION: {
    path: '/safety-confirmation',
    template: async () => getTemplate(import('html/smile-safe/smilesafe-confirmation.html')),
    controller: 'SmileSafeConfirmationCtrl',
    resolve: {
      loadController: () => import('chunk/controller/SmileSafeConfirmationCtrl.js')
    },
    controllerAs: 'smileSafeConfirmation',
    jobRequired: false,
    orderRequired: false,
    orderFlow: false,
    gotoName: 'goToSmileSafeConfirmation'
  },
  SMILESAFE_RECEIPT: {
    path: '/safety-receipt',
    template: async () => getTemplate(import('html/smile-safe/smilesafe-receipt.html')),
    controller: 'SmileSafeReceiptCtrl',
    resolve: {
      loadController: () => import('chunk/controller/SmileSafeReceiptCtrl.js')
    },
    controllerAs: 'smileSafeReceipt',
    jobRequired: false,
    orderFlow: false,
    orderRequired: false,
    gotoName: 'goSmileSafeReceipt'
  },
  SUGGESTEDPRODUCTS: {
    path: '/suggestedProducts',
    template: async () => getTemplate(import('html/addon/suggestedProducts.html')),
    controller: 'SuggestedProductsCtrl',
    resolve: {
      loadController: () => import('chunk/controller/SuggestedProductsCtrl.js')
    },
    controllerAs: 'suggestedProducts',
    gotoName: 'goToSuggestedProducts'
  },
  SMILESAFE_REQUEST: {
    path: '/safety',
    template: async () => getTemplate(import('html/smile-safe/smilesafe-request.html')),
    controller: 'SmileSafeRequestCtrl',
    resolve: {
      loadController: () => import('chunk/controller/SmileSafeRequestCtrl.js')
    },
    controllerAs: 'smileSafeRequest',
    jobRequired: false,
    orderRequired: false,
    orderFlow: false,
    gotoName: 'goSmileSafe'
  },
  STUDENT_ID_INFO: {
    path: '/student-id-info',
    template: async () => getTemplate(import('html/claim/student-id-info.html')),
    controller: 'StudentIdInfoCtrl',
    resolve: {
      loadController: () => import('chunk/controller/StudentIdInfoCtrl.js')
    },
    controllerAs: 'studentIdInfo',
    jobRequired: false,
    orderRequired: false,
    orderFlow: false,
    elevationRequired: false,
    gotoName: 'goToStudentInfo'
  },
  TEXT_PERSONALIZER: {
    path: '/textPersonalizer/:page/:skuCode',
    template: async () => getTemplate(import('html/customize/textPersonalizer.html')),
    controller: 'TextPersonalizerCtrl',
    resolve: {
      loadController: () => import('chunk/controller/TextPersonalizerCtrl.js')
    },
    controllerAs: 'textPersonalizer',
    gotoName: 'goToTextPersonalizer',
    gotoFunc: function (RouterService) {
      return function (page, skuCode) {
        RouterService.goTo(routes.TEXT_PERSONALIZER.path.replace(':page', page).replace(':skuCode', skuCode))();
      };
    }
  },
  TEXT_PERSONALIZER_EDIT: {
    path: '/textPersonalizer/edit/:page/:skuCode/:editIndex',
    template: async () => getTemplate(import('html/customize/textPersonalizer.html')),
    controller: 'TextPersonalizerCtrl',
    resolve: {
      loadController: () => import('chunk/controller/TextPersonalizerCtrl.js')
    },
    controllerAs: 'textPersonalizer',
    gotoName: 'goToEditTextPersonalizer',
    gotoFunc: function (RouterService) {
      return function (page, skuCode, editIndex) {
        RouterService.goTo(
          routes.TEXT_PERSONALIZER_EDIT.path
            .replace(':page', page)
            .replace(':skuCode', skuCode)
            .replace(':editIndex', editIndex)
        )();
      };
    }
  },
  UPDATE_PROFILE: {
    path: '/update-profile',
    template: async () => getTemplate(import('html/account/updateProfile.html')),
    controller: 'UpdateProfileCtrl',
    resolve: {
      loadController: () => import('chunk/controller/UpdateProfileCtrl.js')
    },
    controllerAs: 'updateProfile',
    jobRequired: false,
    orderRequired: false,
    orderFlow: false,
    elevationRequired: true, // check elevation before init as it is not handled on SFLY side
    gotoName: 'goToUpdateProfile'
  },
  WELCOME_BACK: {
    path: '/welcome-back',
    template: async () => getTemplate(import('html/login/welcome-back.html')),
    controller: 'WelcomeBackCtrl',
    resolve: {
      loadController: () => import('chunk/controller/WelcomeBackCtrl.js')
    },
    controllerAs: 'welcomeBackCtrl',
    jobRequired: false,
    orderRequired: false,
    orderFlow: false,
    gotoName: 'goToWelcomeBack'
  },
  SAFETY_CLAIM: {
    path: '/safety/:identifier',
    template: async () => getTemplate(import('html/digital-download/digitalDownload.html')),
    controller: 'DSSKTermsCtrl',
    resolve: {
      loadController: () => import('chunk/controller/DSSKTermsCtrl.js')
    },
    controllerAs: 'digitalDownload',
    jobRequired: false,
    orderRequired: false,
    orderFlow: false,
    gotoName: 'goToDSSKTerms',
    gotoFunc: function (RouterService) {
      return function (identifier) {
        RouterService.goTo(routes.SAFETY_CLAIM.path.replace(':identifier', identifier))();
      };
    }
  },
  ENHANCED_PAY_ONLY: {
    path: '/enhanced-pay-only',
    template: async () => getTemplate(import('html/enhanced-pay-only/enhanced-pay-only.html')),
    controller: 'EnhancedPayOnlyCtrl',
    resolve: {
      loadController: () => import('chunk/controller/EnhancedPayOnlyCtrl.js')
    },
    controllerAs: 'enhancedPayOnly',
    jobRequired: true,
    loginRequired: false,
    orderRequired: false,
    orderFlow: false,
    gotoName: 'goToEnhancedPayOnly'
  },
  ENHANCED_PAY_ONLY_EDIT: {
    path: '/enhanced-pay-only/edit/:editIndex',
    template: async () => getTemplate(import('html/enhanced-pay-only/enhanced-pay-only.html')),
    controller: 'EnhancedPayOnlyCtrl',
    resolve: {
      loadController: () => import('chunk/controller/EnhancedPayOnlyCtrl.js')
    },
    controllerAs: 'enhancedPayOnly',
    jobRequired: true,
    loginRequired: false,
    orderRequired: false,
    orderFlow: false,
    gotoName: 'goToEditEnhancedPayOnly',
    gotoFunc: function (RouterService) {
      return function (editIndex) {
        RouterService.goTo(routes.ENHANCED_PAY_ONLY_EDIT.path.replace(':editIndex', editIndex))();
      };
    }
  },
  ENTER_PASSWORD: {
    path: '/enter-password',
    template: async () => getTemplate(import('html/login/enterPassword.html')),
    controller: 'EnterPasswordCtrl',
    resolve: {
      loadController: () => import('chunk/controller/EnterPasswordCtrl.js')
    },
    controllerAs: 'enterPasswordCtrl',
    jobRequired: false,
    orderRequired: false,
    orderFlow: false,
    gotoName: 'goToEnterPassword'
  },
  EVENT_LIST: {
    path: '/event-list',
    template: async () => getTemplate(import('html/claim/event-list.html')),
    controller: 'EventListCtrl',
    resolve: {
      loadController: () => import('chunk/controller/EventListCtrl.js')
    },
    controllerAs: 'eventListCtrl',
    jobRequired: false,
    orderRequired: false,
    orderFlow: false,
    gotoName: 'goToEventList'
  },
  // Todo: MLC-12165 This old component to be removed in this cleanup task.
  // FIND_MY_PICTURE_DAY: {
  //   path: '/findmypictureday',
  //   template: async () => getTemplate(import('html/find-my-picture-day/find-my-picture-day.html')),
  //   controller: 'FindMyPictureDayCtrl',
  //   resolve: {
  //     loadController: () => import('chunk/controller/FindMyPictureDayCtrl.js')
  //   },
  //   controllerAs: 'findMyPictureDay',
  //   jobRequired: false,
  //   orderRequired: false,
  //   orderFlow: false,
  //   gotoName: 'findMyPictureDay'
  // },
  I_DONT_HAVE_CODE: {
    path: '/i-dont-have-code',
    template: async () => getTemplate(import('html/i-dont-have-code/iDontHaveCode.html')),
    controller: 'IDontHaveCodeCtrl',
    resolve: {
      loadController: () => import('chunk/controller/IDontHaveCodeCtrl.js')
    },
    controllerAs: 'iDontHaveCode',
    jobRequired: false,
    orderRequired: false,
    orderFlow: false,
    gotoName: 'goToIDontHaveCode'
  },
  ENHANCEMENTS_PAGE: {
    path: '/enhancements-page/:skuCode',
    template: async () => getTemplate(import('html/enhancements/enhancements-page.html')),
    controller: 'EnhancementsCtrl',
    resolve: {
      loadController: () => import('chunk/controller/EnhancementsCtrl.js')
    },
    controllerAs: 'enhancementsCtrl',
    packageRequired: true,
    gotoName: 'goToEnhancementsPage',
    gotoFunc: function (RouterService) {
      return function (skuCode) {
        RouterService.goTo(routes.ENHANCEMENTS_PAGE.path.replace(':skuCode', skuCode))();
      };
    }
  },
  ENHANCEMENTS_EDIT_PAGE: {
    path: '/enhancements-page/edit/:editIndex',
    template: async () => getTemplate(import('html/enhancements/enhancements-page.html')),
    controller: 'EnhancementsCtrl',
    resolve: {
      loadController: () => import('chunk/controller/EnhancementsCtrl.js')
    },
    controllerAs: 'enhancementsCtrl',
    packageRequired: true,
    gotoName: 'goToEditEnhancementsPage',
    gotoFunc: function (RouterService) {
      return function (editIndex) {
        RouterService.goTo(routes.ENHANCEMENTS_EDIT_PAGE.path.replace(':editIndex', editIndex))();
      };
    }
  },

  // migrated routes
  SPECIALOFFERS: {
    path: '/bundle-deals',
    template: `
    <lt-special-offers>
      <div lt-content-item zone="SPECIAL_OFFERS_PROMO" promocode="true"></div>
      <lt-breadcrumb></lt-breadcrumb>
    </lt-special-offers>
    `,
    resolve: {
      loadComponent: () => import('app/modules/special-offers/special-offers.component.ts')
    },
    bundleRequired: true,
    gotoName: 'goToSpecialOffers'
  },
  ORDER_HISTORY: {
    path: '/order-history/',
    template: `<lt-order-history-new></lt-order-history-new>`,
    resolve: {
      loadComponent: () => import('app/modules/order-history/order-history.component.ts')
    },
    jobRequired: false,
    loginRequired: true,
    elevationRequired: true,
    orderRequired: false,
    orderFlow: false,
    gotoName: 'goToOrderHistory'
  }
};

var app = angular.module('mlt.mobile', ['ngRoute', 'ngTouch', 'ngAnimate', 'ui.bootstrap', 'toaster', 'ngSanitize']);
app.config([
  '$controllerProvider',
  '$compileProvider',
  '$filterProvider',
  '$provide',
  function ($controllerProvider, $compileProvider, $filterProvider, $provide) {
    app.controller = $controllerProvider.register.bind($controllerProvider);
    app.component = $compileProvider.component.bind($compileProvider);
    app.directive = $compileProvider.directive.bind($compileProvider);
    app.filter = $filterProvider.register.bind($filterProvider);
    app.service = $provide.service.bind($provide);
    app.factory = $provide.factory.bind($provide);
    app.provider = $provide.provider.bind($provide);
  }
]);
app.constant('Config', {
  testMode: false
});

if (typeof window !== 'undefined' && window.__karma__) {
  app.constant('IS_TEST', true);
} else {
  app.constant('IS_TEST', false);
}

app.factory('setCustomHeaders', [
  function () {
    return {
      request: function (request) {
        // don't set no-cache headers for content/ request
        if (request.url.includes(window.assetsUrl) && request.url.includes('.html')) {
          return request;
        }
        if (request.url.indexOf('content/') < 0) {
          request.headers['Cache-Control'] = 'no-cache, max_age=0, no-store';
          request.headers['Pragma'] = 'no-cache';
        }
        return request;
      },
      response: function (response) {
        return response;
      }
    };
  }
]);

app.config([
  '$httpProvider',
  function ($httpProvider) {
    // Initialize get if not there
    if (!$httpProvider.defaults.headers.get) {
      $httpProvider.defaults.headers.get = {};
    }
    $httpProvider.interceptors.push('setCustomHeaders');
  }
]);

app.config([
  '$qProvider',
  function ($qProvider) {
    $qProvider.errorOnUnhandledRejections(false);
  }
]);

app.config([
  '$locationProvider',
  function ($locationProvider) {
    $locationProvider.hashPrefix('');
  }
]);

app.config([
  '$routeProvider',
  ($routeProvider) => {
    Object.values(routes).forEach((routeConfig) => {
      $routeProvider.when(routeConfig.path, routeConfig);
    });

    $routeProvider.otherwise({ redirectTo: routes.HOME.path });
  }
]);
app.config([
  '$sceDelegateProvider',
  '$httpProvider',
  function ($sceDelegateProvider, $httpProvider) {
    $sceDelegateProvider.resourceUrlWhitelist(['self', window.assetsUrl + '**']);
  }
]);

app.service('loggerService', ['$window', '$injector', '$interval', LoggerService]);

app.run([
  'RouterService',
  function (RouterService) {
    for (var key in routes) {
      if (routes.hasOwnProperty(key)) {
        var routeConfig = routes[key];
        RouterService.paths[key] = routeConfig.path;
        if (routeConfig.gotoFunc) {
          RouterService[routeConfig.gotoName] = routeConfig.gotoFunc(RouterService);
        } else {
          RouterService[routeConfig.gotoName] = RouterService.goTo(routeConfig.path);
        }
      }
    }
  }
]);

app.run([
  '$templateCache',
  ($templateCache) => {
    $templateCache.put(POPOVER_TEMPLATE_URL, popoverHtml);
  }
]);

initializeGlobalErrorHandler(app);
initializeRouteLogging(app);
initializeHttpErrorInterceptor(app);

async function getTemplate(promise) {
  return await promise.then((module) => module.default);
}

export default app;
